import { groupIdSchema } from '@lafayette/content';
import { z } from 'zod';

export const instanceIdSchema = z.string().min(1).max(80);
export const playerIdSchema = z.string().min(1).max(80);

/**
 * Targets are chosen client-side and submitted with the play, so the engine
 * validates one complete intent rather than walking a multi-step wizard.
 */
export const actionTargetsSchema = z.object({
  /** Player hit by the action (Environmental Remediation, wild Rent, ...). */
  playerId: playerIdSchema.optional(),
  /** An opponent property or complete mall the action operates on. */
  propertyInstanceId: instanceIdSchema.optional(),
  /** The actor's own property, for 1031 Exchange. */
  ownPropertyInstanceId: instanceIdSchema.optional(),
  /** Group chosen for rent, upgrades, or a complete-mall transfer. */
  group: groupIdSchema.optional(),
});
export type ActionTargets = z.infer<typeof actionTargetsSchema>;

export const commandBodySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('drawForTurn') }),
  z.object({ type: z.literal('bankCard'), cardInstanceId: instanceIdSchema }),
  z.object({
    type: z.literal('playProperty'),
    cardInstanceId: instanceIdSchema,
    group: groupIdSchema,
    /** Invented tenant name for a wildcard. Flavor only; no rules meaning. */
    tenantName: z.string().max(60).optional(),
  }),
  z.object({
    type: z.literal('moveWildcard'),
    cardInstanceId: instanceIdSchema,
    group: groupIdSchema,
  }),
  z.object({
    type: z.literal('playAction'),
    cardInstanceId: instanceIdSchema,
    targets: actionTargetsSchema.default({}),
    /** Renegotiate the Rent cards attached to a rent play. */
    doubleRentInstanceIds: z.array(instanceIdSchema).max(2).default([]),
  }),
  z.object({
    type: z.literal('respondToAction'),
    cancel: z.boolean(),
    cardInstanceId: instanceIdSchema.optional(),
  }),
  z.object({
    type: z.literal('payDebt'),
    debtId: z.string().min(1),
    cardInstanceIds: z.array(instanceIdSchema).max(60),
  }),
  z.object({
    type: z.literal('placeUpgrade'),
    cardInstanceId: instanceIdSchema,
    /** Target mall, or `null` to bank the upgrade as cash. */
    group: groupIdSchema.nullable(),
  }),
  z.object({
    type: z.literal('discardCards'),
    cardInstanceIds: z.array(instanceIdSchema).max(20),
  }),
  z.object({ type: z.literal('cancelEndTurn') }),
  z.object({ type: z.literal('endTurn') }),
]);
export type CommandBody = z.infer<typeof commandBodySchema>;
export type CommandType = CommandBody['type'];

/** A command as it arrives from a client: addressed, versioned, idempotent. */
export const commandSchema = z.object({
  /** Client-generated; replaying the same key is a no-op. */
  idempotencyKey: z.string().min(8).max(80),
  /** The version the client believed it was acting on. */
  expectedVersion: z.number().int().min(0),
  playerId: playerIdSchema,
  body: commandBodySchema,
});
export type Command = z.infer<typeof commandSchema>;

export const REJECTION_REASONS = [
  'not-your-turn',
  'wrong-phase',
  'version-conflict',
  'unknown-card',
  'card-not-in-hand',
  'card-not-playable',
  'illegal-target',
  'no-actions-remaining',
  'must-draw-first',
  'match-finished',
  'invalid-payment',
  'invalid-discard',
  'not-a-responder',
  'already-responded',
  'no-such-debt',
  'not-enough-players',
] as const;

export const rejectionReasonSchema = z.enum(REJECTION_REASONS);
export type RejectionReason = z.infer<typeof rejectionReasonSchema>;
